import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import Masthead from "../components/Masthead";
import RecordSheet from "../components/RecordSheet";
import { fmtDate, statusClass } from "../lib/format";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { ArrowLeft, Search } from "lucide-react";

const DATASET_META = {
  assets: { title: "Assets Inventory", eyebrow: "Drill-down · Inventory" },
  crowdstrike_devices: { title: "CrowdStrike Devices", eyebrow: "Drill-down · EDR" },
  alerts: { title: "Alerts", eyebrow: "Drill-down · Alerts" },
  vulnerabilities: { title: "Vulnerabilities", eyebrow: "Drill-down · Vulns" },
  controls: { title: "Agent Controls", eyebrow: "Drill-down · Controls" },
  control_endpoints: { title: "Control Endpoints", eyebrow: "Drill-down · Endpoints" },
  identities: { title: "Identities", eyebrow: "Drill-down · Identity" },
  incidents: { title: "Incidents", eyebrow: "Drill-down · SOC" },
  firewalls: { title: "Firewalls", eyebrow: "Drill-down · Network" },
  email_threats: { title: "Email Threats", eyebrow: "Drill-down · Email" },
  ai_apps: { title: "GenAI App Catalog", eyebrow: "Drill-down · AI Security" },
  ai_dlp_events: { title: "Prompt-DLP Events", eyebrow: "Drill-down · AI Security" },
  ai_prompt_threats: { title: "Prompt Injection & Jailbreak Events", eyebrow: "Drill-down · AI Security" },
  ai_models: { title: "AI Model Inventory · Risk", eyebrow: "Drill-down · AI-SPM" },
  ai_copilot_flags: { title: "Copilot Governance Flags", eyebrow: "Drill-down · Copilot" },
  ai_governance_controls: { title: "AI Governance Controls", eyebrow: "Drill-down · Governance" },
  cloud_accounts: { title: "Cloud Accounts", eyebrow: "Drill-down · Cloud Security" },
  cloud_misconfigs: { title: "Cloud Misconfigurations", eyebrow: "Drill-down · CSPM" },
  cloud_attack_paths: { title: "Cloud Attack Paths · Toxic Combos", eyebrow: "Drill-down · Cloud Security" },
  cloud_workloads: { title: "Cloud Workloads", eyebrow: "Drill-down · CWPP" },
  cloud_identities: { title: "Cloud Identities", eyebrow: "Drill-down · CIEM" },
  cloud_compliance_controls: { title: "Cloud Compliance Controls", eyebrow: "Drill-down · Compliance" },
  sec_projects: { title: "Security Programme Roll-outs", eyebrow: "Drill-down · Project Mgmt" },
  project_stories: { title: "Sprint Stories · Backlog", eyebrow: "Drill-down · Project Mgmt" },
  support_tickets: { title: "IT Service Desk Tickets", eyebrow: "Drill-down · Support" },
  vendor_tickets: { title: "Vendor / OEM Support Tickets", eyebrow: "Drill-down · Support" },
};

const COLUMNS = {
  assets: ["hostname", "os", "site", "owner", "ip", "encrypted", "managed_by_cs"],
  crowdstrike_devices: ["hostname", "sensor_version", "policy", "status", "aggressive_amp", "confidence"],
  alerts: ["alert_id", "severity", "category", "host", "user", "status", "rule", "score"],
  vulnerabilities: ["cve", "name", "cvss", "domain", "severity", "asset", "age_days", "breached"],
  controls: ["control", "vendor", "kind", "status", "in_scope", "healthy", "unhealthy", "missing"],
  control_endpoints: ["hostname", "owner", "site", "os", "agent_status", "agent_version"],
  identities: ["username", "kind", "department", "mfa_enrolled", "privileged", "leaked_credential", "risk_score"],
  incidents: ["incident_id", "title", "severity", "category", "status", "assignee", "host"],
  firewalls: ["name", "site", "status", "events", "sessions", "allowed", "blocked"],
  email_threats: ["subject", "sender", "recipient", "category", "disposition", "score"],
  ai_apps: ["app", "category", "sanction", "users", "prompts_day", "risk", "dpo_status", "first_seen"],
  ai_dlp_events: ["event_id", "category", "app", "user", "department", "site", "severity", "disposition"],
  ai_prompt_threats: ["threat_id", "kind", "severity", "app", "user", "department", "site", "disposition"],
  ai_models: ["model", "platform", "model_type", "environment", "severity", "guardrails", "pipeline_cves", "owner_team"],
  ai_copilot_flags: ["flag_id", "surface", "user_group", "flag", "severity", "user", "state"],
  ai_governance_controls: ["framework", "control", "status", "owner", "evidence", "last_reviewed"],
  cloud_accounts: ["name", "provider", "native_id", "purpose", "region", "posture_score", "critical_findings", "risk_severity"],
  cloud_misconfigs: ["finding_id", "category", "rule", "severity", "provider", "account", "resource", "status"],
  cloud_attack_paths: ["path_id", "path", "severity", "account", "hops", "impact_asset_type", "state"],
  cloud_workloads: ["workload_id", "kind", "name", "provider", "account", "region", "vulns_critical", "runtime_threats", "exposed_publicly"],
  cloud_identities: ["identity_id", "name", "kind", "provider", "account", "overprivileged", "unused_days", "mfa_enrolled", "risk"],
  cloud_compliance_controls: ["framework", "control", "status", "owner", "evidence", "last_reviewed"],
  sec_projects: ["name", "status", "rollout_pct", "target_pct", "owner_team", "risk", "budget_usd", "budget_spent_pct"],
  project_stories: ["story_key", "title", "priority", "sprint_status", "owner", "project_key", "points"],
  support_tickets: ["ticket_id", "title", "priority", "category", "state", "assignee", "site", "sla_pct", "mttr_hrs"],
  vendor_tickets: ["ticket_id", "vendor", "subject", "priority", "state", "response_hrs", "sla_met", "csat"],
};

export default function Drill() {
  const { dataset } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ rows: [], total: 0 });
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const [page, setPage] = useState(0);
  const limit = 50;

  const meta = DATASET_META[dataset] || { title: dataset, eyebrow: "Drill-down" };
  const cols = COLUMNS[dataset] || [];

  useEffect(() => {
    const filters = {};
    for (const [k, v] of params.entries()) {
      if (k === "q") continue;
      filters[k] = v;
    }
    const search = params.get("q") || undefined;
    apiGet(`/records/${dataset}`, { ...filters, q: search, limit, skip: page * limit })
      .then(setData)
      .catch(() => setData({ rows: [], total: 0 }));
  }, [dataset, params, page]);

  const onSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (q) next.set("q", q);
    else next.delete("q");
    setPage(0);
    setParams(next);
  };

  const renderCell = (col, val) => {
    if (val === true) return "✓";
    if (val === false) return "—";
    if (val == null) return "—";
    if (col === "status" || col === "severity" || col === "disposition" || col === "agent_status" || col === "risk")
      return <span className={`status-pill ${statusClass(String(val))}`}>{val}</span>;
    if (typeof val === "string" && /\d{4}-\d{2}-\d{2}T/.test(val)) return fmtDate(val);
    if (typeof val === "number") return val.toLocaleString();
    return String(val);
  };

  return (
    <>
      <Masthead
        eyebrow={meta.eyebrow}
        title={meta.title}
        subtitle={`Showing ${data.rows.length} of ${data.total.toLocaleString()} records · click any row for the last-leg detail`}
        meta={
          <div>
            <Button
              variant="ghost"
              size="sm"
              data-testid="back-btn"
              onClick={() => navigate(-1)}
              className="small-caps"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </div>
        }
      />
      <div className="px-[26px] py-5 flex-1">
        <form onSubmit={onSearch} className="flex gap-2 mb-4 max-w-md" data-testid="drill-search-form">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
            <Input
              data-testid="drill-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search hostname, CVE, user…"
              className="pl-8"
            />
          </div>
          <Button type="submit" data-testid="drill-search-btn" style={{ background: "var(--cv-crimson)" }}>
            Search
          </Button>
        </form>

        <div className="cv-panel overflow-hidden p-0" data-testid="drill-table-panel">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                  {cols.map((c) => (
                    <th
                      key={c}
                      className="text-left px-3 py-2 small-caps font-bold border-b"
                      style={{ borderColor: "var(--cv-line)" }}
                    >
                      {c.replace(/_/g, " ")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, idx) => (
                  <tr
                    key={row.id || idx}
                    data-testid={`drill-row-${idx}`}
                    className="cv-row-clickable"
                    onClick={() => {
                      setSelected(row);
                      setOpen(true);
                    }}
                  >
                    {cols.map((c) => (
                      <td
                        key={c}
                        className="px-3 py-2 border-b align-top"
                        style={{ borderColor: "var(--cv-line)" }}
                      >
                        {renderCell(c, row[c])}
                      </td>
                    ))}
                  </tr>
                ))}
                {data.rows.length === 0 && (
                  <tr>
                    <td colSpan={cols.length} className="text-center py-6 opacity-60">
                      No records.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-between items-center mt-3 text-sm">
          <div style={{ color: "var(--cv-charcoal-soft)" }}>
            Page {page + 1} of {Math.max(1, Math.ceil(data.total / limit))}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid="prev-page-btn"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="next-page-btn"
              disabled={(page + 1) * limit >= data.total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      <RecordSheet
        open={open}
        onOpenChange={setOpen}
        record={selected}
        title={selected ? selected.hostname || selected.username || selected.cve || selected.incident_id || selected.subject || selected.name || meta.title : meta.title}
      />
    </>
  );
}
